import { sortBy } from "lodash";
import { AssertionError } from "utils/assertions";
import type {
  ProductLineFilter,
  SkuComponentType,
  SkuFilter,
  WeightUnit,
} from "__generated__/gql/graphql";
import { SKU_COMPONENT_TYPES } from "components/Products/SkuPanel/SkuComponents/utils";
import { ArchivedStatus } from "components/Shared/ArchivedStatusButtonGroup";
import { makeFormatWeightUnit } from "components/Shared/weightUnit";
import { formatInteger, makeFormatDecimal } from "utils/formatting";

export const UNARCHIVED_STATUSES = [
  ArchivedStatus.Active,
  ArchivedStatus.All,
] as const;

export function getArchivedSkuFilter(
  archivedStatus: ArchivedStatus,
): SkuFilter | undefined {
  if (archivedStatus === ArchivedStatus.All) return;

  return {
    isArchived: { equalTo: archivedStatus === ArchivedStatus.Archived },
  };
}

export function getArchivedProductLineFilter(
  archivedStatus: ArchivedStatus,
): ProductLineFilter | undefined {
  if (archivedStatus === ArchivedStatus.All) return;

  if (archivedStatus === ArchivedStatus.Active) {
    return { isArchived: { equalTo: false } };
  }

  if (archivedStatus === ArchivedStatus.Archived) {
    return { skus: { some: { isArchived: { equalTo: true } } } };
  }

  throw new AssertionError("Received invalid archived status");
}

export type ProductFilterQueryVariables = {
  productLineFilter?: ProductLineFilter;
  skuFilter?: SkuFilter;
};

export function sortSkuComponents<
  T extends { skuComponentType: SkuComponentType },
>(skuComponents: T[]): T[] {
  return sortBy(skuComponents, (sc) =>
    SKU_COMPONENT_TYPES.indexOf(sc.skuComponentType),
  );
}

export function formatUnitNetWeight(
  unitNetWeight: Decimal | null | undefined,
  unitNetWeightUnit: WeightUnit | null | undefined,
) {
  if (!unitNetWeight) return null;
  const formatter = unitNetWeightUnit
    ? makeFormatWeightUnit(unitNetWeightUnit)
    : makeFormatDecimal({
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      });
  return formatter(unitNetWeight);
}

export function formatCasePack(
  caseQuantity: number | null | undefined,
  innerCaseQuantity?: number | null,
) {
  const parts = [caseQuantity, innerCaseQuantity];
  return parts
    .filter((p) => !!p)
    .map(formatInteger)
    .join(" × ");
}

export const formatWeight = makeFormatDecimal({
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
  style: "unit",
  unit: "pound",
});

export function formatCaseCube(cube: Decimal | null | undefined) {
  if (!cube) return null;
  const baseFormatted = makeFormatDecimal({
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
    style: "unit",
    unit: "foot",
  })(cube);
  return baseFormatted ? `${baseFormatted}³` : null;
}

export function formatTiHi(
  ti: number | null | undefined,
  hi: number | null | undefined,
) {
  if (!ti || !hi) return null;
  return `${formatInteger(ti)} × ${formatInteger(hi)}`;
}
